import { authClient, useSession } from "@d12labs/auth/client";
import { apiFetch, apiUpload } from "@d12labs/client";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@d12labs/ui/alert-dialog";
import { Button, buttonVariants } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { cn } from "@d12labs/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { CircleUser, Copy } from "lucide-react";
// @scaffold:two-factor start
import QRCode from "qrcode";
// @scaffold:two-factor end
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import { type AccentTheme, useTheme } from "@/lib/theme-context";
import { useAvatarUrl } from "@/lib/use-avatar-url";

export function AccountPage() {
	const { data, isPending } = useSession();
	const user = data?.user;
	const currentSessionToken = data?.session?.token ?? null;

	if (isPending) {
		return (
			<PageBody className="p-6">
				<p className="text-sm text-muted-foreground">Loading…</p>
			</PageBody>
		);
	}
	if (!user) return null;

	const copyUserId = () => {
		navigator.clipboard
			.writeText(user.id)
			.then(() => toast.success("User ID copied"))
			.catch(() => toast.error("Couldn't copy to clipboard"));
	};

	// @scaffold:two-factor start
	// twoFactorEnabled comes from the Better Auth user record via the plugin.
	// The plugin extends the user type at runtime; the TS surface inferred from
	// our Drizzle schema doesn't reflect that, so we narrow at the access site.
	const twoFactorEnabled = (user as { twoFactorEnabled?: boolean }).twoFactorEnabled ?? false;
	// @scaffold:two-factor end

	return (
		<>
			<PageHeader
				icon={CircleUser}
				title="Account"
				subtitle="Profile, appearance, password, two-factor and connected sessions."
				actions={
					<Button variant="ghost" size="sm" onClick={copyUserId}>
						<Copy className="size-3.5" />
						Copy user ID
					</Button>
				}
			/>
			<PageBody>
				<ProfileSection
					initialName={user.name ?? ""}
					email={user.email}
					emailVerified={user.emailVerified}
				/>
				<AppearanceSection />
				<PasswordSection />
				{/* @scaffold:two-factor start */}
				<TwoFactorSection enabled={twoFactorEnabled} />
				{/* @scaffold:two-factor end */}
				<SessionsSection currentSessionToken={currentSessionToken} />
				<LoginHistorySection />
				<DangerSection email={user.email} />
			</PageBody>
		</>
	);
}

// Full-bleed settings band: hairline-divided, title + description on the left,
// controls on the right from `md` up; stacked below. The controls column is
// capped so inputs don't stretch across a wide content area.
function AccountSection({
	title,
	description,
	destructive = false,
	children,
}: {
	title: string;
	description: React.ReactNode;
	destructive?: boolean;
	children: React.ReactNode;
}) {
	const headingId = useId();
	return (
		<section
			aria-labelledby={headingId}
			className={cn(
				"grid grid-cols-1 gap-5 border-b border-hairline px-6 py-8 md:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] md:gap-12",
				destructive && "bg-destructive/5",
			)}
		>
			<div className="flex min-w-0 flex-col gap-1.5">
				<h2
					id={headingId}
					className={cn(
						"text-body font-medium",
						destructive ? "text-destructive" : "text-foreground",
					)}
				>
					{title}
				</h2>
				<p className="text-sm text-muted-foreground">{description}</p>
			</div>
			<div className="min-w-0 max-w-xl">{children}</div>
		</section>
	);
}

const ACCENTS: { id: AccentTheme; label: string }[] = [
	{ id: "lavender", label: "Lavender" },
	{ id: "ember", label: "Ember" },
];

function AppearanceSection() {
	const { accent, setAccent } = useTheme();
	return (
		<AccountSection
			title="Appearance"
			description="Accent for actions, links, and focus. Lavender is the default. Warning stays a separate yellow so it does not read as the primary action."
		>
			<div className="flex flex-col gap-3">
				<fieldset className="m-0 inline-flex w-fit min-w-0 rounded-md border border-hairline p-0.5">
					<legend className="sr-only">Accent</legend>
					{ACCENTS.map(({ id, label }) => {
						const selected = accent === id;
						return (
							<button
								key={id}
								type="button"
								aria-pressed={selected}
								onClick={() => setAccent(id)}
								className={
									selected
										? "h-8 rounded px-3 text-body-sm font-medium bg-primary text-primary-foreground"
										: "h-8 rounded px-3 text-body-sm text-ink-muted hover:text-ink"
								}
							>
								{label}
							</button>
						);
					})}
				</fieldset>
				<div className="flex items-center gap-4 text-meta text-ink-subtle">
					<span className="inline-flex items-center gap-2">
						<span className="size-3 rounded-sm bg-primary" />
						Accent
					</span>
					<span className="inline-flex items-center gap-2">
						<span className="size-3 rounded-sm bg-warning" />
						Warning
					</span>
				</div>
			</div>
		</AccountSection>
	);
}

function ProfileSection({
	initialName,
	email,
	emailVerified,
}: {
	initialName: string;
	email: string;
	emailVerified: boolean;
}) {
	const [name, setName] = useState(initialName);
	const [saving, setSaving] = useState(false);

	// Keep local state synced if the upstream user.name changes (e.g. another
	// tab saved). Otherwise the input stays stale until manual edit.
	useEffect(() => {
		setName(initialName);
	}, [initialName]);

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = name.trim();
		if (trimmed.length === 0) {
			toast.error("Name can't be empty");
			return;
		}
		setSaving(true);
		const { error } = await authClient.updateUser({ name: trimmed });
		setSaving(false);
		if (error) {
			toast.error(error.message ?? "Update failed");
			return;
		}
		toast.success("Profile updated");
	};

	return (
		<AccountSection title="Profile" description="How the rest of the app refers to you.">
			<div className="flex flex-col gap-6">
				<AvatarEditor initialName={initialName} email={email} />

				<form onSubmit={handleSave} className="flex flex-col gap-2">
					<Label htmlFor="acct-name">Name</Label>
					<Input
						id="acct-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						autoComplete="name"
					/>
					<div className="flex justify-end">
						<Button type="submit" size="sm" disabled={saving || name.trim() === initialName.trim()}>
							{saving ? "Saving…" : "Save name"}
						</Button>
					</div>
				</form>

				<EmailEditor email={email} emailVerified={emailVerified} />
			</div>
		</AccountSection>
	);
}

function avatarFallbackInitials(name: string, email: string): string {
	const source = name.trim();
	if (source.length > 0) {
		const parts = source.split(/\s+/).filter(Boolean);
		const first = parts[0]?.charAt(0);
		const second = parts[1]?.charAt(0);
		if (first && second) return (first + second).toUpperCase();
		return source.slice(0, 2).toUpperCase();
	}
	return email.slice(0, 2).toUpperCase();
}

function AvatarEditor({ initialName, email }: { initialName: string; email: string }) {
	const queryClient = useQueryClient();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { url, isLoading } = useAvatarUrl();
	const [uploading, setUploading] = useState(false);
	const [removing, setRemoving] = useState(false);

	const hasAvatar = !!url;

	const handlePick = () => fileInputRef.current?.click();

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (fileInputRef.current) fileInputRef.current.value = "";
		if (!file) return;
		if (!file.type.startsWith("image/")) {
			toast.error("Avatar must be an image");
			return;
		}
		setUploading(true);
		try {
			const fd = new FormData();
			fd.append("file", file);
			const { key } = await apiUpload<{ key: string }>("/api/account/avatar", fd);
			const { error } = await authClient.updateUser({ image: key });
			if (error) throw new Error(error.message ?? "Could not save avatar reference");
			queryClient.invalidateQueries({ queryKey: ["account", "avatar-url"] });
			toast.success("Avatar updated");
		} catch (err) {
			toast.error((err as Error).message);
		} finally {
			setUploading(false);
		}
	};

	const handleRemove = async () => {
		setRemoving(true);
		try {
			await apiFetch("/api/account/avatar", { method: "DELETE" });
			const { error } = await authClient.updateUser({ image: null as unknown as string });
			if (error) throw new Error(error.message ?? "Could not clear avatar reference");
			queryClient.invalidateQueries({ queryKey: ["account", "avatar-url"] });
			toast.success("Avatar removed");
		} catch (err) {
			toast.error((err as Error).message);
		} finally {
			setRemoving(false);
		}
	};

	const initials = avatarFallbackInitials(initialName, email);

	return (
		<div className="flex items-center gap-4">
			<div className="size-16 rounded-full overflow-hidden border border-hairline bg-surface-2 flex items-center justify-center text-base font-medium text-ink shrink-0">
				{isLoading ? null : url ? (
					<img src={url} alt="" className="size-full object-cover" />
				) : (
					<span>{initials}</span>
				)}
			</div>
			<div className="flex flex-col gap-1.5">
				<div className="flex gap-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={handlePick}
						disabled={uploading || removing}
					>
						{uploading ? "Uploading…" : hasAvatar ? "Replace" : "Upload"}
					</Button>
					{hasAvatar && (
						<ConfirmDialog
							title="Remove avatar?"
							description="Falls back to your initials."
							confirmLabel="Remove"
							destructive
							onConfirm={handleRemove}
						>
							<Button type="button" variant="ghost" size="sm" disabled={removing}>
								Remove
							</Button>
						</ConfirmDialog>
					)}
				</div>
				<p className="text-xs text-muted-foreground">JPG, PNG, or GIF. Max 2 MB.</p>
				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					onChange={handleFileChange}
					className="hidden"
				/>
			</div>
		</div>
	);
}

function EmailEditor({ email, emailVerified }: { email: string; emailVerified: boolean }) {
	const [editing, setEditing] = useState(false);
	const [newEmail, setNewEmail] = useState("");
	const [sending, setSending] = useState(false);

	const startEdit = () => {
		setNewEmail(email);
		setEditing(true);
	};

	const cancelEdit = () => {
		setEditing(false);
		setNewEmail("");
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = newEmail.trim().toLowerCase();
		if (trimmed.length === 0) {
			toast.error("Email can't be empty");
			return;
		}
		if (trimmed === email.toLowerCase()) {
			toast.error("That's your current email.");
			return;
		}
		setSending(true);
		const { error } = await authClient.changeEmail({
			newEmail: trimmed,
			callbackURL: `${window.location.origin}/account`,
		});
		setSending(false);
		if (error) {
			toast.error(error.message ?? "Could not request email change");
			return;
		}
		setEditing(false);
		setNewEmail("");
		toast.success(`Verification link sent to ${trimmed}. Open it to finish the switch.`);
	};

	if (editing) {
		return (
			<form onSubmit={handleSubmit} className="flex flex-col gap-2">
				<Label htmlFor="acct-new-email">New email</Label>
				<Input
					id="acct-new-email"
					type="email"
					value={newEmail}
					onChange={(e) => setNewEmail(e.target.value)}
					autoComplete="email"
					autoFocus
					required
				/>
				<p className="text-xs text-muted-foreground">
					We'll send a confirmation link to the new address. Your current email stays active until
					you click it.
				</p>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
						Cancel
					</Button>
					<Button
						type="submit"
						size="sm"
						disabled={sending || newEmail.trim().toLowerCase() === email.toLowerCase()}
					>
						{sending ? "Sending…" : "Send verification"}
					</Button>
				</div>
			</form>
		);
	}

	return (
		<div className="flex flex-col gap-2">
			<Label htmlFor="acct-email">Email</Label>
			<div className="flex items-center gap-2">
				<Input id="acct-email" value={email} disabled className="min-w-0" />
				<Button type="button" variant="outline" size="sm" onClick={startEdit}>
					Change
				</Button>
			</div>
			<p className="text-xs text-muted-foreground">
				{emailVerified ? "Verified." : "Not verified — check your inbox for the verification link."}
			</p>
		</div>
	);
}

function PasswordSection() {
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [revokeOthers, setRevokeOthers] = useState(true);
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (currentPassword.length === 0 || newPassword.length < 8) return;
		setSubmitting(true);
		const { error } = await authClient.changePassword({
			currentPassword,
			newPassword,
			revokeOtherSessions: revokeOthers,
		});
		setSubmitting(false);
		if (error) {
			toast.error(error.message ?? "Password change failed");
			return;
		}
		toast.success(
			revokeOthers ? "Password updated — other sessions signed out." : "Password updated.",
		);
		setCurrentPassword("");
		setNewPassword("");
	};

	return (
		<AccountSection
			title="Password"
			description="Change your password. Other sessions sign out by default."
		>
			<form onSubmit={handleSubmit} className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<Label htmlFor="acct-current">Current password</Label>
					<Input
						id="acct-current"
						type="password"
						value={currentPassword}
						onChange={(e) => setCurrentPassword(e.target.value)}
						autoComplete="current-password"
						required
					/>
				</div>
				<div className="flex flex-col gap-2">
					<Label htmlFor="acct-new">New password</Label>
					<Input
						id="acct-new"
						type="password"
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						autoComplete="new-password"
						minLength={8}
						required
					/>
					<p className="text-xs text-muted-foreground">Minimum 8 characters.</p>
				</div>
				<label className="flex items-center gap-2 text-sm">
					<input
						type="checkbox"
						checked={revokeOthers}
						onChange={(e) => setRevokeOthers(e.target.checked)}
						className="h-4 w-4 rounded border border-hairline accent-primary"
					/>
					Sign out all other sessions
				</label>
				<div className="flex justify-end">
					<Button
						type="submit"
						disabled={submitting || currentPassword.length === 0 || newPassword.length < 8}
					>
						{submitting ? "Updating…" : "Update password"}
					</Button>
				</div>
			</form>
		</AccountSection>
	);
}

// @scaffold:two-factor start
function TwoFactorSection({ enabled }: { enabled: boolean }) {
	const [mode, setMode] = useState<"idle" | "enable" | "disable">("idle");

	return (
		<AccountSection
			title="Two-factor authentication"
			description={
				enabled
					? "Active. A 6-digit code is required at sign-in."
					: "Off. Add a second step using an authenticator app like 1Password, Authy, or Google Authenticator."
			}
		>
			<div className="flex flex-col gap-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2 text-sm">
						<span
							className={cn(
								"inline-flex size-2 rounded-full",
								enabled ? "bg-semantic-success" : "bg-ink-tertiary",
							)}
						/>
						{enabled ? "Enabled" : "Disabled"}
					</div>
					{enabled ? (
						<Button variant="outline" size="sm" onClick={() => setMode("disable")}>
							Disable 2FA
						</Button>
					) : (
						<Button variant="outline" size="sm" onClick={() => setMode("enable")}>
							Enable 2FA
						</Button>
					)}
				</div>

				{mode === "enable" && <EnableTwoFactorDialog onClose={() => setMode("idle")} />}
				{mode === "disable" && <DisableTwoFactorDialog onClose={() => setMode("idle")} />}
			</div>
		</AccountSection>
	);
}

function EnableTwoFactorDialog({ onClose }: { onClose: () => void }) {
	type Stage =
		| { name: "password" }
		| { name: "verify"; totpURI: string; backupCodes: string[]; qrDataUrl: string };

	const [open, setOpen] = useState(true);
	const [stage, setStage] = useState<Stage>({ name: "password" });
	const [password, setPassword] = useState("");
	const [code, setCode] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const handleClose = (next: boolean) => {
		setOpen(next);
		if (!next) onClose();
	};

	const handlePasswordSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (password.length === 0) return;
		setSubmitting(true);
		const { data, error } = await authClient.twoFactor.enable({ password });
		setSubmitting(false);
		if (error || !data) {
			toast.error(error?.message ?? "Could not start 2FA setup");
			return;
		}
		// Better Auth 1.7 widened `enable` to a discriminated union — it can now
		// return an email-OTP setup (`{ method: "otp" }`) instead of a TOTP secret.
		// The chassis only wires TOTP, so anything else is a misconfiguration rather
		// than a user error; narrow explicitly instead of reaching for `totpURI`.
		if (data.method !== "totp") {
			toast.error("Unexpected 2FA setup response — expected a TOTP secret.");
			return;
		}
		const { totpURI, backupCodes } = data;
		try {
			const qrDataUrl = await QRCode.toDataURL(totpURI, { margin: 1, width: 256 });
			setStage({ name: "verify", totpURI, backupCodes, qrDataUrl });
			setPassword("");
		} catch (err) {
			toast.error(`Could not render QR code: ${(err as Error).message}`);
		}
	};

	const handleVerifySubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (code.length === 0) return;
		setSubmitting(true);
		const { error } = await authClient.twoFactor.verifyTotp({ code: code.trim() });
		setSubmitting(false);
		if (error) {
			toast.error(error.message ?? "Code didn't verify — check the time on your device");
			return;
		}
		toast.success("Two-factor authentication enabled.");
		// Refresh the session so user.twoFactorEnabled flips on this page.
		await authClient.getSession({ query: { disableCookieCache: true } });
		handleClose(false);
	};

	return (
		<AlertDialog open={open} onOpenChange={handleClose}>
			<AlertDialogContent className="max-w-md">
				<AlertDialogHeader>
					<AlertDialogTitle>
						{stage.name === "password" ? "Enable 2FA" : "Scan and verify"}
					</AlertDialogTitle>
					<AlertDialogDescription>
						{stage.name === "password"
							? "Confirm your password to generate a setup code."
							: "Scan the QR code in your authenticator app, then enter the 6-digit code it generates."}
					</AlertDialogDescription>
				</AlertDialogHeader>

				{stage.name === "password" ? (
					<form onSubmit={handlePasswordSubmit} className="flex flex-col gap-2">
						<Label htmlFor="enable2fa-password">Current password</Label>
						<Input
							id="enable2fa-password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							autoComplete="current-password"
							autoFocus
							required
						/>
						<AlertDialogFooter className="mt-2">
							<AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={(e) => {
									e.preventDefault();
									void handlePasswordSubmit(e as unknown as React.FormEvent);
								}}
								disabled={submitting || password.length === 0}
							>
								{submitting ? "Generating…" : "Continue"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</form>
				) : (
					<form onSubmit={handleVerifySubmit} className="flex flex-col gap-3">
						<div className="flex justify-center">
							<img
								src={stage.qrDataUrl}
								alt="TOTP QR code"
								className="size-48 rounded border border-hairline bg-white p-1"
							/>
						</div>
						<details className="text-xs">
							<summary className="cursor-pointer text-muted-foreground">
								Can't scan? Show setup string
							</summary>
							<code className="block mt-2 break-all rounded bg-surface-2 p-2 font-mono text-meta">
								{stage.totpURI}
							</code>
						</details>

						<div className="rounded border border-hairline p-3">
							<div className="text-xs font-medium mb-2">Backup codes — save these now</div>
							<div className="grid grid-cols-2 gap-1 font-mono text-xs">
								{stage.backupCodes.map((c) => (
									<div key={c}>{c}</div>
								))}
							</div>
							<p className="mt-2 text-meta text-muted-foreground">
								Each code works once. They're your way back in if you lose access to your
								authenticator. You won't see them again after this.
							</p>
						</div>

						<Label htmlFor="enable2fa-code">Code from your authenticator</Label>
						<Input
							id="enable2fa-code"
							inputMode="numeric"
							autoComplete="one-time-code"
							value={code}
							onChange={(e) => setCode(e.target.value)}
							placeholder="123456"
							autoFocus
						/>

						<AlertDialogFooter className="mt-1">
							<AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={(e) => {
									e.preventDefault();
									void handleVerifySubmit(e as unknown as React.FormEvent);
								}}
								disabled={submitting || code.trim().length === 0}
							>
								{submitting ? "Verifying…" : "Enable"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</form>
				)}
			</AlertDialogContent>
		</AlertDialog>
	);
}

function DisableTwoFactorDialog({ onClose }: { onClose: () => void }) {
	const [open, setOpen] = useState(true);
	const [password, setPassword] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const handleClose = (next: boolean) => {
		setOpen(next);
		if (!next) onClose();
	};

	const handleSubmit = async () => {
		if (password.length === 0) return;
		setSubmitting(true);
		const { error } = await authClient.twoFactor.disable({ password });
		setSubmitting(false);
		if (error) {
			toast.error(error.message ?? "Could not disable 2FA");
			return;
		}
		toast.success("Two-factor authentication disabled.");
		await authClient.getSession({ query: { disableCookieCache: true } });
		handleClose(false);
	};

	return (
		<AlertDialog open={open} onOpenChange={handleClose}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Disable 2FA?</AlertDialogTitle>
					<AlertDialogDescription>
						Confirm your password. After disabling, sign-in only requires your password.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<div className="flex flex-col gap-2">
					<Label htmlFor="disable2fa-password">Current password</Label>
					<Input
						id="disable2fa-password"
						type="password"
						value={password}
						onChange={(e) => setPassword(e.target.value)}
						autoComplete="current-password"
						autoFocus
					/>
				</div>
				<AlertDialogFooter>
					<AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
					<AlertDialogAction
						className={cn(buttonVariants({ variant: "destructive" }))}
						onClick={(e) => {
							e.preventDefault();
							void handleSubmit();
						}}
						disabled={submitting || password.length === 0}
					>
						{submitting ? "Disabling…" : "Disable 2FA"}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
// @scaffold:two-factor end

type SessionRow = {
	id: string;
	token: string;
	createdAt: Date | string;
	updatedAt: Date | string;
	expiresAt: Date | string;
	userAgent?: string | null;
	ipAddress?: string | null;
};

function describeUserAgent(ua: string | null | undefined): string {
	if (!ua) return "Unknown device";
	const lower = ua.toLowerCase();
	let os = "Unknown OS";
	if (lower.includes("mac os")) os = "macOS";
	else if (lower.includes("windows")) os = "Windows";
	else if (lower.includes("iphone") || lower.includes("ios")) os = "iOS";
	else if (lower.includes("android")) os = "Android";
	else if (lower.includes("linux")) os = "Linux";

	let browser = "Browser";
	if (lower.includes("edg/")) browser = "Edge";
	else if (lower.includes("chrome/")) browser = "Chrome";
	else if (lower.includes("firefox/")) browser = "Firefox";
	else if (lower.includes("safari/") && !lower.includes("chrome/")) browser = "Safari";

	return `${browser} on ${os}`;
}

function SessionsSection({ currentSessionToken }: { currentSessionToken: string | null }) {
	const queryClient = useQueryClient();
	const { data: sessions, isLoading } = useQuery({
		queryKey: ["auth", "sessions"],
		queryFn: () => authClient.listSessions().then((r) => (r.data ?? []) as unknown as SessionRow[]),
	});

	const invalidate = () => queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] });

	const revokeOne = useMutation({
		mutationFn: async (token: string) => {
			const { error } = await authClient.revokeSession({ token });
			if (error) throw new Error(error.message ?? "Could not revoke session");
		},
		onSuccess: () => {
			invalidate();
			toast.success("Session signed out.");
		},
	});

	const revokeOthers = useMutation({
		mutationFn: async () => {
			const { error } = await authClient.revokeOtherSessions();
			if (error) throw new Error(error.message ?? "Could not revoke sessions");
		},
		onSuccess: () => {
			invalidate();
			toast.success("All other sessions signed out.");
		},
	});

	const otherCount = sessions ? sessions.filter((s) => s.token !== currentSessionToken).length : 0;

	return (
		<AccountSection
			title="Active sessions"
			description="Every device where you're currently signed in. Revoke any you don't recognise."
		>
			<div className="flex flex-col gap-4">
				{isLoading ? (
					<p className="text-sm text-muted-foreground">Loading…</p>
				) : !sessions || sessions.length === 0 ? (
					<p className="text-sm text-muted-foreground">No active sessions.</p>
				) : (
					<ul className="flex flex-col gap-2">
						{sessions.map((s) => {
							const isCurrent = s.token === currentSessionToken;
							return (
								<li
									key={s.id}
									className="border border-hairline rounded p-3 flex items-center justify-between gap-3"
								>
									<div className="min-w-0 flex-1">
										<div className="text-sm font-medium flex items-center gap-2">
											{describeUserAgent(s.userAgent)}
											{isCurrent ? (
												<span className="text-[10px] uppercase tracking-wide rounded-sm bg-accent px-1.5 py-0.5 text-accent-foreground">
													This device
												</span>
											) : null}
										</div>
										<div className="text-xs text-muted-foreground truncate">
											{s.ipAddress ?? "Unknown IP"} · signed in{" "}
											{new Date(s.createdAt).toLocaleString()}
										</div>
									</div>
									{!isCurrent && (
										<Button
											variant="ghost"
											size="sm"
											onClick={() => revokeOne.mutate(s.token)}
											disabled={revokeOne.isPending}
										>
											Revoke
										</Button>
									)}
								</li>
							);
						})}
					</ul>
				)}
				{otherCount > 0 && (
					<div className="flex justify-end">
						<ConfirmDialog
							title={`Sign out ${otherCount} other ${otherCount === 1 ? "session" : "sessions"}?`}
							description="This device stays signed in. Everywhere else, you'll need to sign in again."
							confirmLabel="Sign out others"
							onConfirm={() => revokeOthers.mutate()}
						>
							<Button variant="outline" size="sm" disabled={revokeOthers.isPending}>
								Sign out everywhere else
							</Button>
						</ConfirmDialog>
					</div>
				)}
			</div>
		</AccountSection>
	);
}

type LoginRow = {
	id: string;
	success: boolean;
	ipAddress: string | null;
	userAgent: string | null;
	createdAt: string;
};

function LoginHistorySection() {
	const { data, isLoading } = useQuery({
		queryKey: ["auth", "login-history"],
		queryFn: () => apiFetch<LoginRow[]>("/api/account/login-history"),
	});

	return (
		<AccountSection
			title="Recent sign-ins"
			description="The last few times your account was accessed."
		>
			{isLoading ? (
				<p className="text-sm text-muted-foreground">Loading…</p>
			) : !data || data.length === 0 ? (
				<p className="text-sm text-muted-foreground">No sign-ins recorded yet.</p>
			) : (
				<ul className="flex flex-col">
					{data.map((r, i) => (
						<li
							key={r.id}
							className={cn(
								"flex items-center justify-between gap-4 py-2 text-sm",
								i < data.length - 1 && "border-b border-hairline",
							)}
						>
							<div className="flex min-w-0 flex-col">
								<span>
									{new Date(r.createdAt).toLocaleString(undefined, {
										dateStyle: "medium",
										timeStyle: "short",
									})}
								</span>
								<span className="truncate text-xs text-muted-foreground">
									{r.userAgent ?? "Unknown device"}
								</span>
							</div>
							<span className="flex-none text-xs tabular-nums text-muted-foreground">
								{r.ipAddress ?? "—"}
							</span>
						</li>
					))}
				</ul>
			)}
		</AccountSection>
	);
}

function DangerSection({ email }: { email: string }) {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [password, setPassword] = useState("");
	const [deleting, setDeleting] = useState(false);

	// Clear the password whenever the dialog closes so a re-open starts fresh.
	useEffect(() => {
		if (!open) setPassword("");
	}, [open]);

	const handleDelete = async () => {
		if (password.length === 0) {
			toast.error("Enter your password to confirm.");
			return;
		}
		setDeleting(true);
		const { error } = await authClient.deleteUser({ password });
		setDeleting(false);
		if (error) {
			toast.error(error.message ?? "Delete failed");
			return;
		}
		setOpen(false);
		toast.success("Account deleted.");
		await navigate({ to: "/login" });
	};

	return (
		<AccountSection
			title="Danger zone"
			description="Deleting your account is permanent. All notes, uploads, chats, and CRM data will be removed."
			destructive
		>
			<div className="flex justify-end">
				<Button variant="destructive" onClick={() => setOpen(true)}>
					Delete account
				</Button>
			</div>

			<AlertDialog open={open} onOpenChange={setOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete {email}?</AlertDialogTitle>
						<AlertDialogDescription>
							This action cannot be undone. Type your current password to confirm.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="flex flex-col gap-2">
						<Label htmlFor="delete-confirm-password" className="text-sm">
							Password
						</Label>
						<Input
							id="delete-confirm-password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							autoComplete="current-password"
							autoFocus
						/>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							className={cn(buttonVariants({ variant: "destructive" }))}
							onClick={(e) => {
								// Don't auto-close: we need the dialog open while the mutation
								// is in flight so error feedback stays in context.
								e.preventDefault();
								void handleDelete();
							}}
							disabled={deleting || password.length === 0}
						>
							{deleting ? "Deleting…" : "Delete forever"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</AccountSection>
	);
}
